// Public agreement signing page — mounted BEFORE auth (token IS credential).
// Pattern: same as support-link.ts — self-contained, own API helpers, no global state.

import { API_BASE } from '../api';
import { promptModal } from '../components/modal';

interface ApiResponse<T> { status: 'ok' | 'error'; data?: T; error?: string; }

interface SigningPageInfo {
  agreement_title: string;
  agreement_body: string;
  recipient_name: string;
  recipient_email: string;
  recipient_role: string;
  already_signed: boolean;
  already_declined: boolean;
  is_voided: boolean;
  signature_fields: string[];
}

interface SignConfirmation {
  agreement_title: string;
  signed_at: string;
}

async function apiGet<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  const json = (await res.json()) as ApiResponse<T>;
  if (json.status === 'error') throw new Error(json.error || 'request failed');
  return json.data as T;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
  });
  const json = (await res.json()) as ApiResponse<T>;
  if (json.status === 'error') throw new Error(json.error || 'request failed');
  return json.data as T;
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function renderMd(text: string): string {
  // Lightweight markdown for the signing page (no need for full chat renderer)
  let html = esc(text);
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
  html = html.replace(/\n/g, '<br>');
  return html;
}

export function isPublicSigningPath(): boolean {
  return /\/agreements\/sign\/[^/]+/.test(window.location.pathname);
}

function getToken(): string {
  const m = window.location.pathname.match(/\/agreements\/sign\/([^/]+)/);
  return m?.[1] ?? '';
}

function injectStyles(): void {
  if (document.getElementById('ag-sign-styles')) return;
  const style = document.createElement('style');
  style.id = 'ag-sign-styles';
  style.textContent = `
    :root {
      --ag-bg: var(--surface-0, #1e1f22);
      --ag-surface: var(--surface-1, #2b2d31);
      --ag-text: var(--text-primary, #f2f3f5);
      --ag-text-muted: var(--text-muted, #949ba4);
      --ag-primary: var(--accent, #16a34a);
      --ag-primary-hover: var(--accent-hover, #15803d);
      --ag-danger: var(--danger, #dc2626);
      --ag-danger-hover: var(--danger, #dc2626);
      --ag-success: var(--accent, #16a34a);
      --ag-border: var(--border-subtle, #3f4147);
      --ag-yellow: var(--star, #f0b72f);
      --ag-card-bg: var(--surface-2, #313338);
    }
    * { margin:0; padding:0; box-sizing:border-box; }
    body { background:var(--ag-bg); color:var(--ag-text); font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif; min-height:100vh; }
    .ag-sign-page { max-width:800px; margin:0 auto; padding:24px 16px; }
    .ag-sign-header { text-align:center; padding:32px 0 24px; border-bottom:1px solid var(--ag-border); margin-bottom:24px; }
    .ag-sign-header h1 { font-size:24px; font-weight:600; margin-bottom:8px; }
    .ag-sign-header .ag-sign-subtitle { color:var(--ag-text-muted); font-size:14px; }
    .ag-sign-recipient { background:var(--ag-card-bg); border:1px solid var(--ag-border); border-radius:8px; padding:16px; margin-bottom:24px; display:flex; align-items:center; gap:12px; }
    .ag-sign-recipient-icon { width:40px; height:40px; border-radius:50%; background:var(--ag-primary); display:flex; align-items:center; justify-content:center; color:var(--fg-on-accent, #fff); font-weight:600; font-size:18px; }
    .ag-sign-recipient-info { flex:1; }
    .ag-sign-recipient-name { font-weight:600; font-size:16px; }
    .ag-sign-recipient-email { color:var(--ag-text-muted); font-size:13px; }
    .ag-sign-document { background:var(--ag-surface); border:1px solid var(--ag-border); border-radius:8px; padding:24px; margin-bottom:24px; line-height:1.7; }
    .ag-sign-document h2 { font-size:18px; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid var(--ag-border); }
    .ag-sign-fields { background:var(--ag-card-bg); border:2px solid var(--ag-yellow); border-radius:8px; padding:20px; margin-bottom:24px; }
    .ag-sign-fields h3 { font-size:16px; margin-bottom:16px; color:var(--ag-yellow); }
    .ag-sign-field-group { margin-bottom:16px; }
    .ag-sign-field-group label { display:block; font-size:13px; color:var(--ag-text-muted); margin-bottom:6px; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; }
    .ag-sign-field-group input { width:100%; padding:12px; background:var(--ag-surface); border:1px solid var(--ag-border); border-radius:6px; color:var(--ag-text); font-size:16px; }
    .ag-sign-field-group input:focus { outline:none; border-color:var(--ag-primary); }
    .ag-sign-typed-preview { font-family:'Dancing Script','Brush Script MT',cursive; font-size:32px; color:var(--ag-primary); padding:12px 0; min-height:50px; }
    .ag-sign-actions { display:flex; gap:12px; justify-content:center; padding:24px 0; }
    .ag-sign-actions button { padding:12px 32px; border:none; border-radius:8px; font-size:16px; font-weight:600; cursor:pointer; transition:background 0.2s; }
    .ag-sign-btn-primary { background:var(--ag-primary); color:var(--fg-on-accent, #fff); }
    .ag-sign-btn-primary:hover { background:var(--ag-primary-hover); }
    .ag-sign-btn-primary:disabled { opacity:0.5; cursor:not-allowed; }
    .ag-sign-btn-danger { background:transparent; color:var(--ag-danger); border:1px solid var(--ag-danger) !important; }
    .ag-sign-btn-danger:hover { background:var(--ag-danger); color:var(--fg-on-accent, #fff); }
    .ag-sign-adopt { background:var(--ag-card-bg); border:1px solid var(--ag-border); border-radius:8px; padding:16px; margin-bottom:24px; }
    .ag-sign-adopt p { font-size:14px; color:var(--ag-text-muted); margin-bottom:12px; }
    .ag-sign-adopt label { display:flex; align-items:center; gap:8px; cursor:pointer; font-size:14px; }
    .ag-sign-adopt input[type=checkbox] { width:18px; height:18px; accent-color:var(--ag-primary); }
    .ag-sign-confirmation { text-align:center; padding:48px 0; }
    .ag-sign-confirmation .ag-check-circle { width:80px; height:80px; border-radius:50%; background:var(--ag-success); display:inline-flex; align-items:center; justify-content:center; margin-bottom:24px; }
    .ag-sign-confirmation .ag-check-circle svg { width:48px; height:48px; color:var(--fg-on-accent, #fff); }
    .ag-sign-confirmation h2 { font-size:28px; margin-bottom:12px; }
    .ag-sign-confirmation p { color:var(--ag-text-muted); font-size:16px; margin-bottom:24px; }
    .ag-sign-confirmation a { display:inline-block; padding:12px 24px; background:var(--ag-primary); color:var(--fg-on-accent, #fff); text-decoration:none; border-radius:8px; font-weight:600; }
    .ag-sign-error { text-align:center; padding:48px 0; color:var(--ag-text-muted); }
    .ag-sign-error h2 { font-size:24px; margin-bottom:12px; color:var(--ag-text); }
    .ag-sign-voided { border-color:var(--ag-danger); }
    .ag-sign-voided-badge { display:inline-block; background:var(--ag-danger); color:var(--fg-on-accent, #fff); padding:4px 12px; border-radius:4px; font-size:13px; font-weight:600; margin-bottom:16px; }
    .ag-sign-already { background:var(--ag-card-bg); border:1px solid var(--ag-success); border-radius:8px; padding:20px; text-align:center; margin-bottom:24px; }
    .ag-sign-already h3 { color:var(--ag-success); margin-bottom:8px; }
    @media (max-width: 600px) {
      .ag-sign-page { padding:16px 12px; }
      .ag-sign-actions { flex-direction:column; }
      .ag-sign-actions button { width:100%; }
    }
  `;
  document.head.appendChild(style);
}

export function renderAgreementSign(): void {
  injectStyles();
  const app = document.getElementById('app');
  if (!app) return;
  const token = getToken();

  app.innerHTML = '<div class="ag-sign-page"><div class="ag-sign-header"><h1>Loading...</h1></div></div>';

  apiGet<SigningPageInfo>(`/api/public/agreements/sign/${encodeURIComponent(token)}`)
    .then(info => renderSigningPage(app, token, info))
    .catch(err => {
      app.innerHTML = `
        <div class="ag-sign-page">
          <div class="ag-sign-error">
            <h2>Signing Link Unavailable</h2>
            <p>${esc(String(err))}</p>
          </div>
        </div>`;
    });
}

function renderSigningPage(app: HTMLElement, token: string, info: SigningPageInfo): void {
  if (info.is_voided) {
    app.innerHTML = `
      <div class="ag-sign-page">
        <div class="ag-sign-header">
          <h1>${esc(info.agreement_title)}</h1>
          <div class="ag-sign-subtitle">This agreement has been voided by the sender.</div>
        </div>
        <div class="ag-sign-document ag-sign-voided">
          <span class="ag-sign-voided-badge">VOIDED</span>
          <div>${renderMd(info.agreement_body)}</div>
        </div>
      </div>`;
    return;
  }

  if (info.already_signed) {
    app.innerHTML = `
      <div class="ag-sign-page">
        <div class="ag-sign-header">
          <h1>${esc(info.agreement_title)}</h1>
        </div>
        <div class="ag-sign-already">
          <h3>Already Signed</h3>
          <p>You have already signed this agreement.</p>
        </div>
        <div class="ag-sign-document">
          <div>${renderMd(info.agreement_body)}</div>
        </div>
      </div>`;
    return;
  }

  if (info.already_declined) {
    app.innerHTML = `
      <div class="ag-sign-page">
        <div class="ag-sign-header">
          <h1>${esc(info.agreement_title)}</h1>
        </div>
        <div class="ag-sign-already" style="border-color:var(--ag-danger)">
          <h3 style="color:var(--ag-danger)">Declined</h3>
          <p>You have declined this agreement.</p>
        </div>
      </div>`;
    return;
  }

  const initial = (info.recipient_name || info.recipient_email || '?').charAt(0).toUpperCase();

  app.innerHTML = `
    <div class="ag-sign-page">
      <div class="ag-sign-header">
        <h1>Review and Sign</h1>
        <div class="ag-sign-subtitle">Please review the document below and sign to complete.</div>
      </div>

      <div class="ag-sign-recipient">
        <div class="ag-sign-recipient-icon">${esc(initial)}</div>
        <div class="ag-sign-recipient-info">
          <div class="ag-sign-recipient-name">${esc(info.recipient_name || 'Signer')}</div>
          <div class="ag-sign-recipient-email">${esc(info.recipient_email)}</div>
        </div>
        <span style="font-size:13px;color:var(--ag-text-muted);text-transform:uppercase">${esc(info.recipient_role)}</span>
      </div>

      <div class="ag-sign-document">
        <h2>${esc(info.agreement_title)}</h2>
        <div>${renderMd(info.agreement_body)}</div>
      </div>

      <div class="ag-sign-fields">
        <h3>Your Signature</h3>
        <div class="ag-sign-field-group">
          <label for="sig-typed">Type your full legal name</label>
          <input id="sig-typed" type="text" placeholder="Type your name to sign" autocomplete="name">
        </div>
        <div class="ag-sign-typed-preview" id="sig-preview"></div>
        <div class="ag-sign-field-group">
          <label>Date</label>
          <input type="text" readonly value="${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}">
        </div>
      </div>

      <div class="ag-sign-adopt">
        <p>By clicking "Adopt and Sign", I agree that the signature will be the electronic representation of my signature for all purposes when I use them on documents, including legally binding contracts.</p>
        <label><input type="checkbox" id="sig-adopt"> I agree to use electronic records and signatures</label>
      </div>

      <div class="ag-sign-actions">
        <button class="ag-sign-btn-primary" id="sig-submit" disabled>Adopt and Sign</button>
        <button class="ag-sign-btn-danger" id="sig-decline">Decline to Sign</button>
      </div>
    </div>`;

  // Wire events
  const typedInput = document.getElementById('sig-typed') as HTMLInputElement;
  const preview = document.getElementById('sig-preview') as HTMLElement;
  const adoptCheck = document.getElementById('sig-adopt') as HTMLInputElement;
  const submitBtn = document.getElementById('sig-submit') as HTMLButtonElement;
  const declineBtn = document.getElementById('sig-decline') as HTMLButtonElement;

  function updateSubmitState(): void {
    const hasName = typedInput.value.trim().length > 0;
    const adopted = adoptCheck.checked;
    submitBtn.disabled = !(hasName && adopted);
  }

  typedInput.addEventListener('input', () => {
    preview.textContent = typedInput.value;
    updateSubmitState();
  });

  adoptCheck.addEventListener('change', updateSubmitState);

  submitBtn.addEventListener('click', () => {
    submitBtn.disabled = true;
    submitBtn.textContent = 'Signing...';
    apiPost<SignConfirmation>(`/api/public/agreements/sign/${encodeURIComponent(token)}/submit`, {
      signature_type: 'typed',
      signature_data: typedInput.value.trim(),
    }).then(conf => {
      renderConfirmation(app, conf);
    }).catch(err => {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Adopt and Sign';
      const errDiv = document.createElement('div');
      errDiv.style.cssText = 'color:var(--ag-danger);text-align:center;padding:8px;font-size:14px';
      errDiv.textContent = String(err);
      submitBtn.parentElement?.appendChild(errDiv);
    });
  });

  declineBtn.addEventListener('click', () => {
    promptModal('Decline agreement', 'Reason for declining (optional)', (reason) => {
      declineBtn.disabled = true;
      declineBtn.textContent = 'Declining...';
      apiPost<SignConfirmation>(`/api/public/agreements/sign/${encodeURIComponent(token)}/decline`, {
        reason: reason || '',
      }).then(() => {
      app.innerHTML = `
        <div class="ag-sign-page">
          <div class="ag-sign-confirmation">
            <h2>Agreement Declined</h2>
            <p>You have declined to sign "${esc(info.agreement_title)}".</p>
            <a href="/">Continue to Hub People</a>
          </div>
        </div>`;
      }).catch(err => {
        declineBtn.disabled = false;
        declineBtn.textContent = 'Decline to Sign';
        const errDiv = document.createElement('div');
        errDiv.style.cssText = 'color:var(--ag-danger);text-align:center;padding:8px;font-size:14px';
        errDiv.textContent = String(err);
        declineBtn.parentElement?.appendChild(errDiv);
      });
    });
  });
}

function renderConfirmation(app: HTMLElement, conf: SignConfirmation): void {
  const date = new Date(conf.signed_at).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
  app.innerHTML = `
    <div class="ag-sign-page">
      <div class="ag-sign-confirmation">
        <div class="ag-check-circle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
        <h2>You Signed "${esc(conf.agreement_title)}"</h2>
        <p>Signed on ${esc(date)}</p>
        <a href="/">Continue to Hub People</a>
      </div>
    </div>`;
}
